import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  UserCheck,
  Users,
  CalendarCheck,
  FileText,
  Calendar,
  Bell,
  Award,
  LogOut,
  ShieldAlert,
  Menu,
  X,
  Sun,
  Moon,
  ExternalLink,
} from "lucide-react";

export const AdminLayout = () => {
  const { currentUser, userProfile, logout, theme, toggleTheme } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { label: "Dashboard", path: "/admin", icon: <LayoutDashboard className="w-4 h-4" /> },
    { label: "Registrations", path: "/admin/registrations", icon: <UserCheck className="w-4 h-4" /> },
    { label: "Students", path: "/admin/students", icon: <Users className="w-4 h-4" /> },
    { label: "Attendance", path: "/admin/attendance", icon: <CalendarCheck className="w-4 h-4" /> },
    { label: "Materials", path: "/admin/materials", icon: <FileText className="w-4 h-4" /> },
    { label: "Schedule", path: "/admin/schedule", icon: <Calendar className="w-4 h-4" /> },
    { label: "Announcements", path: "/admin/announcements", icon: <Bell className="w-4 h-4" /> },
    { label: "Certificates", path: "/admin/certificates", icon: <Award className="w-4 h-4" /> },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center space-x-2">
          <ShieldAlert className="w-5 h-5 text-emerald-400" />
          <span className="font-bold text-sm tracking-tight">Kian Admin Console</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded-lg border border-border text-foreground"
          >
            {theme === "dark" ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-700" />}
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-1.5 rounded-lg border border-border text-foreground"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Sidebar Navigation */}
      <aside
        className={`${
          mobileOpen ? "block" : "hidden"
        } md:block w-full md:w-64 border-r border-border bg-card/95 md:min-h-screen flex flex-col justify-between p-4 z-30`}
      >
        <div className="space-y-6">
          <div className="hidden md:flex items-center space-x-3 px-2 py-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <ShieldAlert className="w-4 h-4" />
            </div>
            <div>
              <div className="font-bold text-sm tracking-tight flex items-center gap-1.5">
                <span>Kian Tech</span>
                <span className="text-[10px] uppercase font-mono px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-400">
                  Admin
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground font-mono">
                15D Cyber Masterclass
              </div>
            </div>
          </div>

          {/* Admin User Badge */}
          <div className="p-3 rounded-xl bg-muted/50 border border-border flex items-center space-x-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-400 font-mono text-xs font-bold flex items-center justify-center">
              AD
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate text-foreground">
                {userProfile?.fullName || currentUser?.email?.split("@")[0] || "Administrator"}
              </div>
              <div className="text-[10px] font-mono text-emerald-400">
                Staff Authority
              </div>
            </div>
          </div>

          <nav className="space-y-1">
            {navItems.map((item) => {
              const active =
                item.path === "/admin"
                  ? location.pathname === "/admin"
                  : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg text-xs font-mono font-medium transition-all ${
                    active
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shadow-sm"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground border border-transparent"
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="pt-6 border-t border-border space-y-2">
          <div className="hidden md:flex items-center justify-between px-3 py-1 text-xs text-muted-foreground font-mono">
            <span>Theme</span>
            <button
              onClick={toggleTheme}
              className="p-1 rounded border border-border hover:bg-muted text-foreground"
            >
              {theme === "dark" ? <Sun className="w-3.5 h-3.5 text-amber-400" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-xs font-mono font-medium text-destructive hover:bg-destructive/10 border border-transparent hover:border-destructive/20 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Admin Viewport */}
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
};
